import { Fragment, useMemo } from 'react';
import {
  ChevronLeft, MapPin, User as UserIcon, Languages, Award,
  Sun, Users, UserCheck, Sparkles, Mail, Phone, ExternalLink,
  ShieldCheck, Home as HomeIcon, Calendar, DollarSign, Star,
} from 'lucide-react';
import { useCoach, useCoachReviews } from '../../hooks/useAPI';
import FavoriteButton from './FavoriteButton';

interface CoachDetailScreenProps {
  coachId: string;
  onNavigate: (screen: any, params?: any) => void;
}

type LessonType = 'private' | 'semi_private' | 'group';
type Environment = 'indoor' | 'outdoor';

const LESSON_LABELS: Record<LessonType, { label: string; icon: any }> = {
  private: { label: 'Private', icon: UserIcon },
  semi_private: { label: 'Semi-private', icon: UserCheck },
  group: { label: 'Group', icon: Users },
};

const TITLE_LABELS: Record<string, string> = {
  tennis_coach: 'Tennis Coach',
  head_coach: 'Head Coach',
  junior_development_coach: 'Junior Development Coach',
  assistant_coach: 'Assistant Coach',
  performance_coach: 'Performance Coach',
};
const AUDIENCE_LABELS: Record<string, string> = {
  men: 'Men', women: 'Women',
  boys: 'Boys', girls: 'Girls',
  mixed_groups: 'Mixed groups', families: 'Families',
};
const PROGRAM_LABELS: Record<string, string> = {
  private_lessons: 'Private lessons',
  group_classes: 'Group classes',
  kids_classes: 'Kids classes',
  ladies_classes: 'Ladies classes',
  performance_training: 'Performance training',
};
const OPP_POLICY_LABELS: Record<string, { label: string; tone: 'ok' | 'warn' | 'no' }> = {
  available: { label: 'Available for opposite-gender students', tone: 'ok' },
  upon_request: { label: 'Available upon request', tone: 'warn' },
  not_available: { label: 'Not available for opposite-gender students', tone: 'no' },
};

/**
 * Coach profile screen. Slice 2A reduced this to info-only — the
 * booking funnel lives at /coaches/:coachId/book in BookCoachScreen.
 * Tapping the hero CTA or any rate-card cell navigates there
 * (with the rate-card cell pre-selecting that combo via params).
 */
export default function CoachDetailScreen({
  coachId, onNavigate,
}: CoachDetailScreenProps) {
  const { data: coach } = useCoach(coachId);
  const { data: reviews } = useCoachReviews(coachId);
  const reviewList = (reviews as any[]) || [];
  const avgRating = reviewList.length
    ? reviewList.reduce((s, r) => s + r.rating, 0) / reviewList.length
    : null;
  const c = coach as any;

  const rates = (c?.coach_lesson_types ?? []).filter((r: any) => r.is_active) as any[];

  // Pick the 2 highest-rated reviews with non-empty comments for the
  // hero snippet block.
  const topReviews = useMemo(() => {
    const ranked = [...reviewList].sort((a, b) =>
      (b.rating - a.rating) || (b.created_at ?? '').localeCompare(a.created_at ?? ''),
    );
    const withComments = ranked.filter((r) => r.comment?.trim());
    return (withComments.length >= 2 ? withComments : ranked).slice(0, 2);
  }, [reviewList]);

  if (!c) {
    return (
      <div className="w-full h-screen bg-background flex items-center justify-center text-sm text-muted-foreground">
        Loading coach…
      </div>
    );
  }

  const goBook = (params?: { lessonType?: LessonType; environment?: Environment }) =>
    onNavigate('book-coach', { id: coachId, ...(params ?? {}) });

  return (
    <div className="w-full min-h-screen bg-background pb-20">
      {/* Full-width photo hero */}
      <div className="relative aspect-[4/3] bg-secondary">
        {c.photo_url ? (
          <img src={c.photo_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <UserIcon className="w-20 h-20 text-muted-foreground/40" />
          </div>
        )}
        <button
          onClick={() => onNavigate('coaches')}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/95 text-foreground flex items-center justify-center shadow-sm"
          aria-label="Back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="absolute top-4 right-4">
          <FavoriteButton target={{ kind: 'coach', id: c.id }} variant="floating" />
        </div>
        {avgRating !== null && (
          <div className="absolute bottom-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 text-xs shadow-sm">
            <Star className="w-3 h-3 fill-[#D4F04A] text-[#D4F04A]" />
            <span className="font-medium">{avgRating.toFixed(1)}</span>
            <span className="text-muted-foreground">· {reviewList.length}</span>
          </div>
        )}
      </div>

      {/* Identity block */}
      <div className="px-6 pt-6">
        <h1 className="text-2xl leading-tight">{c.display_name}</h1>
        <div className="text-sm text-muted-foreground mt-1">
          {coachTitleLabel(c) || (c.type === 'freelance' ? 'Freelance coach' : 'Staff coach')}
        </div>
        {c.clubs?.name && (
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
            <MapPin className="w-3 h-3" /> {c.clubs.name}
            {c.clubs.address ? ` · ${c.clubs.address}` : ''}
          </div>
        )}

        {/* Hero CTA: routes to the dedicated booking screen. */}
        <button
          type="button"
          onClick={() => goBook()}
          className="mt-4 w-full sm:w-auto h-11 px-5 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium inline-flex items-center justify-center gap-2"
        >
          <Calendar className="w-4 h-4" /> Book a session
        </button>
      </div>

      {/* Hero review snippets */}
      {topReviews.length > 0 && (
        <div className="px-6 pt-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {topReviews.map((r: any) => (
              <ReviewSnippet key={r.id} review={r} />
            ))}
          </div>
        </div>
      )}

      {/* Stat strip */}
      {(c.years_experience || c.nationality || (c.gender && c.gender !== 'prefer_not_to_say') || (c.languages?.length ?? 0) > 0) && (
        <div className="mx-6 mt-5 grid grid-cols-2 sm:grid-cols-4 rounded-2xl border border-border bg-card overflow-hidden">
          <StatCell label="Experience" value={c.years_experience ? `${c.years_experience}y` : '—'} />
          <StatCell label="Nationality" value={c.nationality ?? '—'} />
          <StatCell label="Gender" value={
            c.gender === 'male' ? 'Male'
            : c.gender === 'female' ? 'Female'
            : c.gender === 'other' ? 'Other'
            : '—'
          } />
          <StatCell label="Languages" value={(c.languages?.length ?? 0) === 0 ? '—' : String(c.languages.length)} />
        </div>
      )}

      {/* Rate card — taps route to the booking screen with the
          combination pre-selected. */}
      {rates.length > 0 && (
        <div className="px-6 pt-5">
          <RateCardGrid
            rates={rates}
            onPick={(lt, env) => goBook({ lessonType: lt, environment: env })}
          />
        </div>
      )}

      {/* Profile content cards */}
      <div className="px-6 pt-5 pb-4 space-y-4">
        {c.bio && (
          <ProfileCard title="About">
            <p className="text-sm leading-relaxed text-foreground/80">{c.bio}</p>
          </ProfileCard>
        )}

        {(c.available_to_coach ?? []).length > 0 && (
          <ProfileCard title="Coaches" icon={<Users className="w-4 h-4 text-[#1A6B4F]" />}>
            <div className="flex flex-wrap gap-2">
              {(c.available_to_coach ?? []).map((a: string) => (
                <span key={a} className="px-2.5 py-1 rounded-full bg-secondary text-xs">
                  {AUDIENCE_LABELS[a] ?? a}
                </span>
              ))}
            </div>
          </ProfileCard>
        )}

        {c.opposite_gender_policy && (
          <OppPolicyCallout
            policy={c.opposite_gender_policy}
            note={c.opposite_gender_policy_note}
          />
        )}

        {(c.programs ?? []).length > 0 && (
          <ProfileCard title="Programs" icon={<Sparkles className="w-4 h-4 text-[#1A6B4F]" />}>
            <div className="flex flex-wrap gap-2">
              {(c.programs ?? []).map((p: string) => (
                <span key={p} className="px-2.5 py-1 rounded-full bg-secondary text-xs">
                  {PROGRAM_LABELS[p] ?? p}
                </span>
              ))}
            </div>
          </ProfileCard>
        )}

        {(c.qualifications ?? []).length > 0 && (
          <ProfileCard title="Qualifications" icon={<Award className="w-4 h-4 text-[#1A6B4F]" />}>
            <ul className="space-y-2">
              {(c.qualifications ?? []).map((q: string, i: number) => (
                <li key={`${q}-${i}`} className="flex items-start gap-2 text-sm text-foreground/80">
                  <ShieldCheck className="w-4 h-4 text-[#1A6B4F] shrink-0 mt-0.5" />
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </ProfileCard>
        )}

        {(c.specialties ?? []).length > 0 && (
          <ProfileCard title="Specialties">
            <div className="flex flex-wrap gap-2">
              {(c.specialties ?? []).map((s: string) => (
                <span key={s} className="px-2.5 py-1 rounded-full bg-secondary text-xs">{s}</span>
              ))}
            </div>
          </ProfileCard>
        )}

        {(c.languages ?? []).length > 0 && (
          <ProfileCard title="Languages" icon={<Languages className="w-4 h-4 text-[#1A6B4F]" />}>
            <div className="text-sm text-foreground/80">
              {(c.languages ?? []).join(' · ')}
            </div>
          </ProfileCard>
        )}

        <CoachContactBlock c={c} />

        {reviewList.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
              <Star className="w-4 h-4 fill-[#D4F04A] text-[#D4F04A]" />
              {avgRating?.toFixed(1)} · {reviewList.length} {reviewList.length === 1 ? 'review' : 'reviews'}
            </h3>
            <div className="space-y-2">
              {reviewList.slice(0, 5).map((r: any) => (
                <div key={r.id} className="bg-card border border-border rounded-2xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`w-3.5 h-3.5 ${
                            n <= r.rating ? 'fill-[#D4F04A] text-[#D4F04A]' : 'text-border'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {r.profiles?.full_name ?? 'Deuce player'} · {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  {r.comment && <p className="text-sm">{r.comment}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sticky bottom CTA — single Book a session action since this
          screen no longer hosts the booking form itself. */}
      <div className="fixed bottom-20 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border px-4 py-3">
        <button
          type="button"
          onClick={() => goBook()}
          className="w-full h-12 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium inline-flex items-center justify-center gap-2"
        >
          <Calendar className="w-4 h-4" /> Book a session
        </button>
      </div>
    </div>
  );
}

/**
 * 2×3 rate-card grid: lesson type (private / semi / group) on the
 * vertical axis, environment (indoor / outdoor) on the horizontal.
 * Empty cells render dashed and unclickable. Tapping a populated
 * cell routes to the booking screen with the combination
 * pre-selected via params.
 */
function RateCardGrid({
  rates, onPick,
}: {
  rates: Array<{
    id: string;
    lesson_type: LessonType;
    environment: Environment;
    per_person_price: number;
    currency: string;
    session_minutes: number;
  }>;
  onPick: (lt: LessonType, env: Environment) => void;
}) {
  const lookup = (lt: LessonType, env: Environment) =>
    rates.find((r) => r.lesson_type === lt && r.environment === env);
  const types: LessonType[] = ['private', 'semi_private', 'group'];
  const envs: Environment[] = ['indoor', 'outdoor'];
  const currency = rates[0]?.currency ?? 'AED';

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b border-border flex items-center justify-between">
        <div className="text-sm font-medium flex items-center gap-2">
          <DollarSign className="w-4 h-4 text-[#1A6B4F]" /> Rates per person
        </div>
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
          Tap to book
        </div>
      </div>
      <div className="grid grid-cols-3">
        <div />
        {envs.map((env) => (
          <div key={env}
            className="px-3 py-2 text-[10px] uppercase tracking-wider text-muted-foreground bg-secondary/40 flex items-center gap-1">
            {env === 'indoor'
              ? <HomeIcon className="w-3 h-3" />
              : <Sun className="w-3 h-3" />}
            {env === 'indoor' ? 'Indoor' : 'Outdoor'}
          </div>
        ))}
        {types.map((lt) => {
          const { label, icon: Icon } = LESSON_LABELS[lt];
          return (
            <Fragment key={lt}>
              <div className="px-3 py-3 text-xs font-medium border-t border-border bg-secondary/40 flex items-center gap-1.5">
                <Icon className="w-3.5 h-3.5 text-[#1A6B4F]" /> {label}
              </div>
              {envs.map((env) => {
                const rate = lookup(lt, env);
                return (
                  <button
                    key={`${lt}-${env}`}
                    type="button"
                    disabled={!rate}
                    onClick={() => rate && onPick(lt, env)}
                    className={`px-3 py-3 border-t border-l border-border text-left transition-colors ${
                      rate
                        ? 'hover:bg-[#D4F04A]/10 active:bg-[#D4F04A]/20 cursor-pointer'
                        : 'bg-secondary/20 text-muted-foreground cursor-default'
                    }`}
                  >
                    {rate ? (
                      <>
                        <div className="text-sm font-semibold text-[#1A6B4F]">
                          {currency} {Number(rate.per_person_price).toFixed(0)}
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-0.5">
                          {rate.session_minutes} min
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-muted-foreground">—</div>
                    )}
                  </button>
                );
              })}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

function ReviewSnippet({ review }: { review: any }) {
  return (
    <div className="bg-card border border-border rounded-xl p-3">
      <div className="flex items-center gap-1 mb-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <Star
            key={n}
            className={`w-3 h-3 ${
              n <= review.rating ? 'fill-[#D4F04A] text-[#D4F04A]' : 'text-border'
            }`}
          />
        ))}
        <span className="text-[10px] text-muted-foreground ml-1">
          {review.profiles?.full_name ?? 'Deuce player'}
        </span>
      </div>
      {review.comment && (
        <p className="text-xs leading-snug text-foreground/80 line-clamp-3">
          &ldquo;{review.comment}&rdquo;
        </p>
      )}
    </div>
  );
}

function StatCell({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 border-r border-b last:border-r-0 sm:last:border-r-0 sm:[&:nth-child(4)]:border-r-0 sm:[&:nth-child(-n+4)]:border-b-0 border-border">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-sm font-medium text-foreground mt-0.5 truncate">{value}</div>
    </div>
  );
}

function ProfileCard({
  title, icon, children,
}: { title: string; icon?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-4">
      <div className="text-sm font-medium mb-2 flex items-center gap-2">
        {icon}
        {title}
      </div>
      {children}
    </div>
  );
}

function OppPolicyCallout({ policy, note }: { policy: string; note: string | null | undefined }) {
  const meta = OPP_POLICY_LABELS[policy as keyof typeof OPP_POLICY_LABELS];
  if (!meta) return null;
  const toneClass =
    meta.tone === 'ok'
      ? 'text-green-900 bg-green-50 border-green-200'
      : meta.tone === 'warn'
        ? 'text-amber-900 bg-amber-50 border-amber-200'
        : 'text-gray-700 bg-gray-50 border-gray-200';
  return (
    <div className={`rounded-2xl border p-3 ${toneClass}`}>
      <div className="text-sm font-medium flex items-center gap-2">
        <ShieldCheck className="w-4 h-4" />
        {meta.label}
      </div>
      {note && <div className="text-xs opacity-80 mt-1">{note}</div>}
    </div>
  );
}

function coachTitleLabel(c: any): string | null {
  if (!c?.title) return null;
  if (c.title === 'other') return c.title_other?.trim() || null;
  return TITLE_LABELS[c.title] ?? null;
}

function CoachContactBlock({ c }: { c: any }) {
  const email = (c.contact_email as string | null) ?? null;
  const phone = (c.contact_phone as string | null) ?? null;
  const link  = (c.booking_link  as string | null) ?? null;
  if (!email && !phone && !link) return null;

  return (
    <div>
      <h3 className="text-sm font-medium mb-2">Contact coach directly</h3>
      <div className="space-y-2">
        {phone && (
          <a href={`tel:${phone}`}
            className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border text-sm hover:border-[#1A6B4F]/40">
            <Phone className="w-4 h-4 text-[#1A6B4F]" />
            <span>{phone}</span>
          </a>
        )}
        {email && (
          <a href={`mailto:${email}`}
            className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border text-sm hover:border-[#1A6B4F]/40">
            <Mail className="w-4 h-4 text-[#1A6B4F]" />
            <span className="truncate">{email}</span>
          </a>
        )}
        {link && (
          <a href={link} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-3 p-3 rounded-xl bg-card border border-border text-sm hover:border-[#1A6B4F]/40">
            <ExternalLink className="w-4 h-4 text-[#1A6B4F]" />
            <span className="truncate">{link}</span>
          </a>
        )}
      </div>
    </div>
  );
}
